import React, {
  useEffect,
  useState,
} from 'react';

import {
  Navigate,
} from 'react-router-dom';

import {
  useSession,
} from './SessionContext';

import {
  supabase,
} from './supabaseClient';

import {
  membershipAllowsForRole,
} from './membership';

import {
  useOrganisationSubscriptionTier,
} from './useOrganisationSubscriptionTier';

function getUserRole(user) {
  return String(
    user?.user_metadata?.role ||
      user?.app_metadata?.role ||
      user?.role ||
      ''
  ).toLowerCase();
}

export default function ProtectedRoute({
  children,
  requiredFeature,
}) {
  const session = useSession();

  const {
    organisationSubscriptionTier,
    organisationSubscriptionLoading,
  } = useOrganisationSubscriptionTier(
    session?.user
  );

  const [
    accountLoading,
    setAccountLoading,
  ] = useState(true);

  const [
    accountActive,
    setAccountActive,
  ] = useState(true);

  const [
    hasOrganisationAccess,
    setHasOrganisationAccess,
  ] = useState(true);

  const [
    databaseRole,
    setDatabaseRole,
  ] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function checkAccountStatus() {
      if (!session?.user?.id) {
        if (!cancelled) {
          setAccountLoading(false);
          setAccountActive(true);
          setHasOrganisationAccess(true);
          setDatabaseRole('');
        }

        return;
      }

      setAccountLoading(true);

      const {
        data,
        error,
      } = await supabase
        .from('users')
        .select(
          'role, is_active, organisation_id, organisation_code'
        )
        .eq(
          'auth_id',
          session.user.id
        )
        .maybeSingle();

      if (cancelled) {
        return;
      }

      if (error) {
        setAccountActive(true);
        setHasOrganisationAccess(true);
        setDatabaseRole('');
        setAccountLoading(false);
        return;
      }

      const hasOrganisationId =
        Boolean(
          String(
            data?.organisation_id || ''
          ).trim()
        );

      const hasOrganisationCode =
        Boolean(
          String(
            data?.organisation_code || ''
          ).trim()
        );

      setAccountActive(
        data?.is_active !== false
      );

      setHasOrganisationAccess(
        hasOrganisationId &&
          hasOrganisationCode
      );

      setDatabaseRole(
        String(
          data?.role || ''
        ).toLowerCase()
      );

      setAccountLoading(false);
    }

    checkAccountStatus();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  if (session === undefined) {
    return (
      <div
        style={{
          color: '#b6eaff',
          padding: '2rem',
        }}
      >
        Restoring session...
      </div>
    );
  }

  if (!session) {
    return (
      <Navigate
        to="/register"
        replace
      />
    );
  }

  if (accountLoading) {
    return (
      <div
        style={{
          color: '#b6eaff',
          padding: '2rem',
        }}
      >
        Checking account status...
      </div>
    );
  }

  if (!hasOrganisationAccess) {
    return (
      <div
        style={{
          maxWidth: 620,
          margin: '4rem auto',
          background: '#181a2a',
          color: 'white',
          borderRadius: 16,
          padding: '2rem',
          boxShadow:
            '0 4px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2
          style={{
            color: '#ff8d8d',
            marginTop: 0,
          }}
        >
          Account Access
        </h2>

        <p
          style={{
            color: '#d7ebff',
            lineHeight: 1.6,
          }}
        >
          You are no longer a member of an
          organisation.
        </p>

        <p
          style={{
            color: '#d7ebff',
            lineHeight: 1.6,
            marginTop: '0.9rem',
          }}
        >
          If you believe this is incorrect, please
          contact your organisation administrator.
        </p>
      </div>
    );
  }

  if (!accountActive) {
    return (
      <div
        style={{
          maxWidth: 620,
          margin: '4rem auto',
          background: '#181a2a',
          color: 'white',
          borderRadius: 16,
          padding: '2rem',
          boxShadow:
            '0 4px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2
          style={{
            color: '#ff8d8d',
            marginTop: 0,
          }}
        >
          Account Suspended
        </h2>

        <p
          style={{
            color: '#d7ebff',
            lineHeight: 1.6,
          }}
        >
          Your account has been suspended.
        </p>

        <p
          style={{
            color: '#d7ebff',
            lineHeight: 1.6,
            marginTop: '0.9rem',
          }}
        >
          Please contact your organisation
          administrator.
        </p>
      </div>
    );
  }

  const userRole =
    databaseRole ||
    getUserRole(session.user);

  if (userRole === 'platform_admin') {
    return children;
  }

  if (
    organisationSubscriptionLoading
  ) {
    return (
      <div
        style={{
          color: '#b6eaff',
          padding: '2rem',
        }}
      >
        Loading organisation package...
      </div>
    );
  }

  if (!organisationSubscriptionTier) {
    return (
      <div
        style={{
          color: '#b6eaff',
          padding: '2rem',
        }}
      >
        Loading organisation package...
      </div>
    );
  }

  if (
    !membershipAllowsForRole(
      organisationSubscriptionTier,
      requiredFeature,
      userRole
    )
  ) {
    const featureQuery =
      requiredFeature
        ? `?feature=${encodeURIComponent(
            requiredFeature
          )}`
        : '';

    return (
      <Navigate
        to={`/upgrade${featureQuery}`}
        replace
        state={{
          membershipTier:
            organisationSubscriptionTier,
        }}
      />
    );
  }

  return children;
}